import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ICON_PATHS, IconName, resolveIconName } from './icon-paths';

export type IconSize =
  | 'xs'
  | 'sm'
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | '4xl'
  | '5xl'
  | 'inherit';

/**
 * GAP-FE-PRIME · `<ui-icon>` — single inline-SVG primitive that
 * replaces the deprecated `<i class="pi pi-X">` icon-font pattern
 * across the codebase. The icon font is NOT loaded anywhere in this
 * repo; all 2,175 prior `pi pi-*` usages would render as empty inline
 * elements if not migrated. This component inlines the SVG `<path>`
 * data for every icon name used so the glyph is visible without any
 * font dep.
 *
 * Usage:
 *   <ui-icon name="check-circle" size="md" aria-hidden="true" />
 *   <ui-icon name="exclamation-triangle" size="sm"
 *            class="text-bb-accent-amber" />
 *
 * Sizes follow the Tailwind h-N/w-N scale:
 *   xs    →  3 (12px)
 *   sm    →  4 (16px) — default for buttons / chips
 *   md    →  5 (20px)
 *   lg    →  6 (24px) — default for standalone icons
 *   xl    →  8 (32px)
 *   2xl   → 10 (40px)
 *   3xl   → 12 (48px) — empty-state / loading-state hero icons
 *   4xl   → 16 (64px) — large empty-state hero icons
 *   inherit → no size class (caller is RESPONSIBLE for sizing the
 *             inner <svg>; using `class="w-N h-N"` on the
 *             <ui-icon> host element will NOT size the rendered
 *             SVG because the template overrides the inner SVG's
 *             class binding. Prefer the 3xl/4xl tiers above for
 *             non-standard hero sizes; only use `inherit` when the
 *             caller injects its own sizing via inline style on the
 *             host. — GAP-FE-D08 r2.
 *
 * Colour: the SVG uses `stroke="currentColor"` so the icon inherits
 * its parent's text colour. Wrap with `class="text-bb-accent-X"` to
 * tint.
 *
 * Animation: pass `spin` to rotate continuously (replaces the
 * legacy `pi pi-spin` modifier on legacy icon-font).
 */
@Component({
  selector: 'ui-icon',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // GAP-FE-UX17 batch-3 r1/r2/r3 — display-target for host transforms.
  //   r1: host: { class: 'inline-block' } clobbered caller .block utility
  //       (same specificity, source-order loss).
  //   r2: :host {...} compiles to [_nghost-XXX] (0,1,0) AND is injected
  //       after Tailwind, so it beat caller utilities by source order.
  //   r3 (this): wrap in :where() to ZERO the selector specificity
  //       (0,0,0). ANY caller display utility — .block, .hidden,
  //       responsive md:flex, etc. (0,1,0) — now wins over the default,
  //       while the rule still establishes inline-block as the fallback
  //       so [class.rotate-90] / scale transforms remain functional.
  styles: [':where(:host) { display: inline-block; }'],
  template: `<svg
      [attr.class]="hostClasses()"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      [attr.aria-hidden]="ariaHidden()"
      [attr.role]="role()"
      [attr.focusable]="'false'"
      [innerHTML]="pathHtml()">
    </svg>`,
})
export class IconComponent {
  private readonly sanitizer = inject(DomSanitizer);

  /** Canonical icon name (without the legacy `pi pi-` prefix). */
  readonly name = input.required<string>();
  /** Sizing tier; defaults to lg (24px) to match heroicons native viewBox. */
  readonly size = input<IconSize>('lg');
  /**
   * Mark the SVG aria-hidden=true when decorative (default true).
   * GAP-FE-PRIME-P4 r1 (Codex) · `booleanAttribute` transform lets
   * callers use a bare presence form (`<ui-icon decorative>`).
   * Without the transform, Angular passes the bare attribute as the
   * empty string and the falsy default-coerce keeps it false.
   */
  readonly decorative = input<boolean, boolean | string>(true, { transform: booleanAttribute });
  /**
   * Spin animation (replaces legacy `pi pi-spin`).
   * GAP-FE-PRIME-P4 r1 (Codex) · `booleanAttribute` transform so
   * `<ui-icon spin>` (bare presence) is correctly interpreted as
   * true. Pre-transform, bare attributes resolved to an empty
   * string and the spinner stopped animating.
   */
  readonly spin = input<boolean, boolean | string>(false, { transform: booleanAttribute });

  private readonly sizeClasses: Record<IconSize, string> = {
    xs: 'h-3 w-3',
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-6 w-6',
    xl: 'h-8 w-8',
    '2xl': 'h-10 w-10',
    // GAP-FE-D08 r2: hero-size tiers for empty-state / loading-state
    // / error-state icons that previously lived as inline SVG with
    // `w-12 h-12` / `w-16 h-16` Tailwind utilities.
    '3xl': 'h-12 w-12',
    '4xl': 'h-16 w-16',
    // GAP-FE-UX17 batch-3 r1 P3: 96px hero tier for the largest
    // empty-state and brand-mark icons that previously lived as
    // inline SVG with `w-24 h-24` Tailwind utilities (e.g.,
    // schema-comparison no-comparison state).
    '5xl': 'h-24 w-24',
    inherit: '',
  };

  protected readonly hostClasses = computed(() => {
    const size = this.sizeClasses[this.size()];
    const animation = this.spin() ? 'animate-spin' : '';
    return `inline-block flex-shrink-0 ${size} ${animation}`.trim().replace(/\s+/g, ' ');
  });

  protected readonly ariaHidden = computed(() => this.decorative() ? 'true' : null);
  protected readonly role = computed(() => this.decorative() ? null : 'img');

  /**
   * GAP-FE-PRIME · Resolve the name to its <path> data, sanitise
   * the SVG markup (defense-in-depth — path data is hardcoded but
   * we still pass it through DomSanitizer.bypassSecurityTrustHtml
   * via the SafeHtml type), and bind to [innerHTML]. The wrapping
   * <svg> attributes live on the template; only the inner content
   * comes from the path map.
   */
  protected readonly pathHtml = computed<SafeHtml>(() => {
    const canonical: IconName = resolveIconName(this.name());
    const def = ICON_PATHS[canonical];
    // eslint-disable-next-line no-restricted-syntax -- inlined SVG path data is repo-controlled (icon-paths.ts), not user input.
    return this.sanitizer.bypassSecurityTrustHtml(def.path);
  });
}
